// Display formatting for FA's date columns.
//
// FA's flattened episode query carries SERVICE_START_DATE and its siblings as
// STRINGS — the adapter marshals an Oracle DATE and what lands on the wire is
// "2026-08-04T00:00:00.000Z". The proto types them as strings for that reason,
// and every view rendered them verbatim: the review header's DOS read as a
// machine timestamp, and so did the batch case list's service-date column.
//
// # Why this is not `new Date(s).toLocaleDateString()`
//
// FA stamps a service date at MIDNIGHT UTC. Constructing a Date from that and
// formatting it converts to the reader's timezone, and every US zone is behind
// UTC — so August 4th renders as August 3rd. A date-of-service off by one day is
// invisible on screen and changes which codes are correct, which makes it
// exactly the kind of wrong a reviewer would act on.
//
// So the day, month and year are read out of the STRING and never passed through
// a timezone. The time part is discarded rather than rendered: FA's is midnight
// on every row measured, so showing it would add noise that is always the same.

/** Y-M-D at the head of a string, with any time part after it. */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]|$)/;

/**
 * One FA date column, formatted for a person.
 *
 * Returns "" for missing or sentinel dates and passes through anything it does
 * not recognise. Passing through is deliberate: a formatter that blanks what it
 * cannot parse turns a value the reviewer could still have read into missing
 * data, and FA's columns are not uniform enough to bet on.
 */
export function faDate(value: string | undefined | null): string {
  if (!value) return "";
  const s = value.trim();
  if (!s) return "";

  const m = ISO_DATE.exec(s);
  if (!m) return s;

  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);

  // A zero or year-1 date is a sentinel, not a date. Rendering it yields
  // "1/1/1", which reads as an absurd real date rather than as absent — the
  // same failure tsToDateString guards against on the timestamp side.
  if (!year || !month || !day || year <= 1) return "";

  return `${month}/${day}/${year}`;
}

/**
 * A service date range, collapsed to one string.
 *
 * A single day when the two ends agree, which is nearly every anesthesia case;
 * "8/4/2026 – 8/5/2026" when it spans midnight. Either end alone is shown on
 * its own rather than half a range.
 */
export function faDateRange(
  start: string | undefined | null,
  end: string | undefined | null,
): string {
  const a = faDate(start);
  const b = faDate(end);
  if (!a) return b;
  if (!b || a === b) return a;
  return `${a} – ${b}`;
}
